import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../config/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/** Fetch current user's role from public.user_roles ('admin' | 'client'). Returns null if not found. */
async function fetchUserRole() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data, error } = await supabase
    .from('user_roles')
    .select('role')
    .eq('user_id', user.id)
    .maybeSingle();
  if (error) {
    console.error('Error fetching user role:', error);
    return null;
  }
  if (Array.isArray(data) && data.length === 0) return null;
  const raw = data?.role ?? (Array.isArray(data) ? data[0]?.role : null) ?? null;
  const role = typeof raw === 'string' ? raw.trim().toLowerCase() : null;
  return role === 'admin' || role === 'client' || role === 'stakeholder' ? role : null;
}

const ROLE_FETCH_TIMEOUT_MS = 10000;

/** Same as fetchUserRole but gives up after ROLE_FETCH_TIMEOUT_MS so login doesn't hang. */
function fetchUserRoleWithTimeout() {
  return Promise.race([
    fetchUserRole(),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('ROLE_FETCH_TIMEOUT')), ROLE_FETCH_TIMEOUT_MS)
    ),
  ]).catch((err) => {
    if (err?.message === 'ROLE_FETCH_TIMEOUT') {
      console.warn('Fetching user role timed out. Check Supabase user_roles table and RLS.');
      return null;
    }
    throw err;
  });
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null); // 'admin' | 'client' | 'stakeholder' | null
  const roleFetchInFlightRef = useRef(false);
  const currentRoleFetchPromiseRef = useRef(null);

  /** Single place for role fetch. If one is in flight, returns that same promise so we never double up. Returns Promise<role | null>. */
  const fetchRoleAndSetState = useCallback(() => {
    if (currentRoleFetchPromiseRef.current) return currentRoleFetchPromiseRef.current;
    roleFetchInFlightRef.current = true;
    const promise = fetchUserRoleWithTimeout()
      .then((userRole) => {
        setRole(userRole);
        return userRole;
      })
      .catch((err) => {
        console.warn('Role fetch failed:', err);
        setRole(null);
        return null;
      })
      .finally(() => {
        roleFetchInFlightRef.current = false;
        currentRoleFetchPromiseRef.current = null;
      });
    currentRoleFetchPromiseRef.current = promise;
    return promise;
  }, []);

  const AUTH_LOAD_TIMEOUT_MS = 5000; // If session check hangs, stop loading so user can see login quickly.

  const loadSessionAndRole = useCallback(async () => {
    setLoading(true);
    const timeoutId = setTimeout(() => {
      setLoading((prev) => {
        if (prev) {
          console.warn('Auth session check timed out — showing login. Check .env and network.');
          return false;
        }
        return prev;
      });
      setUser(null);
      setRole(null);
      setIsAuthenticated(false);
    }, AUTH_LOAD_TIMEOUT_MS);
    try {
      let { data: { session }, error } = await supabase.auth.getSession();
      if (!session && !error) {
        const { data: refreshData, error: refreshErr } = await supabase.auth.refreshSession();
        if (!refreshErr && refreshData?.session) {
          session = refreshData.session;
        } else if (refreshErr) {
          // Stale or revoked refresh token — clear it locally so it doesn't cause repeated errors
          await supabase.auth.signOut({ scope: 'local' });
        }
      }
      if (error) {
        console.error('Error checking user session:', error);
        setUser(null);
        setRole(null);
        setIsAuthenticated(false);
        return;
      }
      if (!session) {
        setUser(null);
        setRole(null);
        setIsAuthenticated(false);
        return;
      }
      setUser(session.user);
      setIsAuthenticated(true);
      clearTimeout(timeoutId);
      setLoading(false);
      fetchRoleAndSetState();
    } catch (err) {
      console.error('Error in loadSessionAndRole:', err);
      setUser(null);
      setRole(null);
      setIsAuthenticated(false);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, [fetchRoleAndSetState]);

  useEffect(() => {
    loadSessionAndRole();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadSessionAndRole();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_OUT') {
          setUser(null);
          setRole(null);
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }
        if (!session) {
          const { data: refreshData, error: refreshErr } = await supabase.auth.refreshSession();
          if (!refreshErr && refreshData?.session) {
            setUser(refreshData.session.user);
            setIsAuthenticated(true);
            setLoading(false);
            fetchRoleAndSetState();
            return;
          }
          if (refreshErr) {
            // Stale or revoked refresh token — clear it locally and sign out cleanly
            await supabase.auth.signOut({ scope: 'local' });
            setUser(null);
            setRole(null);
            setIsAuthenticated(false);
            setLoading(false);
            return;
          }
          // Avoid spurious logout: re-check once after a short delay (e.g. race during token refresh)
          const { data: { session: recheck } } = await new Promise((resolve) => {
            setTimeout(() => resolve(supabase.auth.getSession()), 400);
          });
          if (recheck) {
            setUser(recheck.user);
            setIsAuthenticated(true);
            setLoading(false);
            fetchRoleAndSetState();
            return;
          }
          setUser(null);
          setRole(null);
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          setUser(session.user);
          setIsAuthenticated(true);
          setLoading(false);
          fetchRoleAndSetState();
        }
      }
    );

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      subscription.unsubscribe();
    };
  }, [loadSessionAndRole, fetchRoleAndSetState]);

  const LOGIN_TOTAL_TIMEOUT_MS = 14000;

  const login = async (email, password) => {
  const doLogin = async () => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      return { success: false, error: error.message };
    }

    if (!data.user) {
      return { success: false, error: 'Login failed' };
    }

    setUser(data.user);
    setIsAuthenticated(true);
    await new Promise((r) => setTimeout(r, 300));
    const userRole = await fetchRoleAndSetState();
    return { success: true, role: userRole, user: data.user };
  };

    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('LOGIN_TOTAL_TIMEOUT')), LOGIN_TOTAL_TIMEOUT_MS)
    );

    try {
      return await Promise.race([doLogin(), timeoutPromise]);
    } catch (error) {
      if (error?.message === 'LOGIN_TOTAL_TIMEOUT') {
        return {
          success: false,
          error: 'Verifying your account timed out. If you just signed in, try refreshing the page — you may already be logged in.',
        };
      }
      const message = error?.message || 'An error occurred';
      const isNetworkError = message.includes('fetch') || message.includes('NetworkError') || error?.name === 'TypeError';
      return {
        success: false,
        error: isNetworkError
          ? 'Cannot reach Supabase. Check REACT_APP_SUPABASE_URL in .env, restart the dev server, and ensure your Supabase project is not paused (Dashboard → Settings → General).'
          : message,
      };
    }
  };

  /** Re-fetch role from DB and update state. Use when isAuthenticated but role may be stale/null. Forces a new fetch when called (e.g. "Try again" on Login). */
  const refetchRole = useCallback(() => {
    currentRoleFetchPromiseRef.current = null;
    fetchRoleAndSetState();
  }, [fetchRoleAndSetState]);

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setRole(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  /** Update password and clear must_change_password. Call after first-login change. */
  const updatePassword = async (newPassword) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
        data: { must_change_password: false },
      });
      if (error) return { success: false, error: error.message };
      setUser(data?.user ?? user);
      return { success: true };
    } catch (err) {
      return { success: false, error: err?.message || 'Failed to update password' };
    }
  };

  const value = {
    isAuthenticated,
    user,
    role, // 'admin' | 'client' | 'stakeholder' | null
    login,
    logout,
    updatePassword,
    refetchRole,
    loading,
    isAdmin: role === 'admin',
    isStakeholder: role === 'stakeholder',
    isVendr: role === 'client',
    mustChangePassword: !!user?.user_metadata?.must_change_password,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

